import React, { useState } from 'react';
import { Text, Link, Form, Select, Button, Flex } from '@heartinternet/hi-ui';
import type { HostAccount, UpgradeGrid } from '../types';
import type { Package } from '../../ManagePackagesPage/types';
import { MigrateModal } from '../../ManagePackagesPage/components/MigrateModal';
import { Pro2SharedUpgradeModal } from './Pro2SharedUpgradeModal';
import { PremiumUpsellModal } from './PremiumUpsellModal';

const MANAGE_HOSTING_CGI = '/manage/manage_hosting.cgi';

/** specific_types for which the legacy pro2shared "Upgrade" button is enabled. */
const PRO2SHARED_TYPES = ['package.starter', 'package.home', 'package.business'];

type OpenModal = null | 'pro2shared' | 'upsell' | 'migrate';

interface AccessExtendSectionProps {
  hostAccounts: HostAccount[];
  isReseller: boolean;
  migrationPro2Shared: boolean;
  premiumMigrationEnabled: boolean;
  hasPremiumHosting: boolean;
  upgradeGrid?: UpgradeGrid;
  salesEmail?: string;
}

/** Option value submitted as `account` — `free-`-prefixed for free packages. */
const optionValue = (a: HostAccount): string => (a.isFree ? `free-${a.oid}` : a.oid);

/** Adapt a HostAccount to the shape MigrateModal reads (oid/login/short_domain/is_hemsx/web_server_type). */
const toMigratable = (a: HostAccount): Package => ({
  active: 1,
  date_added_s: '',
  login: a.domain,
  oid: a.oid,
  primary_domain_oid: '',
  primary_domain_status: '',
  resold_type_name: '',
  short_domain: a.domain,
  web_server: '',
  web_server_type: a.os === 'windows' ? 'windows' : 'normal',
  initial_resold_type: 0,
  // Plumbed from the CGI (host_accounts_json). MigrateModal filters out
  // is_hemsx === 1 so an already-premium package can't be "migrated to
  // premium" — hardcoding 0 here would defeat that guard when a customer with
  // premium hosting selects one of their existing premium sites.
  is_hemsx: a.isHemsx ? 1 : 0,
  migration: null,
});

export const AccessExtendSection: React.FC<AccessExtendSectionProps> = ({
  hostAccounts,
  isReseller,
  migrationPro2Shared,
  premiumMigrationEnabled,
  hasPremiumHosting,
  upgradeGrid,
  salesEmail,
}) => {
  const hasAccounts = hostAccounts.length > 0;
  const [selectedValue, setSelectedValue] = useState<string>(
    hasAccounts ? optionValue(hostAccounts[0]) : ''
  );
  const [openModal, setOpenModal] = useState<OpenModal>(null);

  const selected = hostAccounts.find((a) => optionValue(a) === selectedValue);
  const isWindows = selected?.os === 'windows';
  const pro2sharedEligible = Boolean(
    selected?.specificType && PRO2SHARED_TYPES.includes(selected.specificType)
  );

  // Which upgrade/migrate control shows, in legacy precedence order.
  const showMigrate = premiumMigrationEnabled && hasPremiumHosting;
  const showUpsell = premiumMigrationEnabled && !hasPremiumHosting;
  const showPro2shared = !premiumMigrationEnabled && migrationPro2Shared && pro2sharedEligible;

  return (
    <>
      <Text variant="body">
        Select your domain name and web hosting package from the drop down list and click
        &ldquo;Access eXtend&rdquo; to manage your web hosting account. This will open the eXtend
        control panel in a new window. From there you can manage your email and FTP settings,
        install CGI scripts, examine your website logs, and generally manage your website.
      </Text>

      <Form
        action={MANAGE_HOSTING_CGI}
        method="post"
        target="_blank"
        className="hi-ui-manage-hosting-page__access-form"
      >
        <Flex direction="row" gap="md" align="center" wrap>
          {hasAccounts ? (
            <Select
              name="account"
              aria-label="Web hosting account"
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
            >
              {hostAccounts.map((a) => (
                <option key={optionValue(a)} value={optionValue(a)} title={a.domain}>
                  {a.account}
                  {a.status === 'TRANSFER' ? ' (awaiting transfer)' : ''}
                </option>
              ))}
            </Select>
          ) : (
            <Text variant="body" inline>
              <strong>
                You have no purchased hosting packages
                {isReseller ? (
                  <>
                    ; <Link href="/manage/reseller/index.cgi">click here</Link> if you&apos;re
                    looking for your resold packages
                  </>
                ) : null}
                .
              </strong>
            </Text>
          )}

          <Button type="submit" variant="primary" disabled={!hasAccounts}>
            Access eXtend
          </Button>

          {hasAccounts && showMigrate && (
            <Button
              type="button"
              variant="secondary"
              disabled={isWindows}
              onClick={() => setOpenModal('migrate')}
            >
              Migrate to Premium Hosting
            </Button>
          )}
          {hasAccounts && showUpsell && (
            <Button
              type="button"
              variant="secondary"
              disabled={isWindows}
              onClick={() => setOpenModal('upsell')}
            >
              Upgrade to Premium Hosting
            </Button>
          )}
          {hasAccounts && showPro2shared && (
            <Button type="button" variant="secondary" onClick={() => setOpenModal('pro2shared')}>
              Upgrade
            </Button>
          )}
          {hasAccounts && (showMigrate || showUpsell) && isWindows && (
            <Text variant="body-small" inline>
              * Linux only
            </Text>
          )}
        </Flex>
      </Form>

      {premiumMigrationEnabled && (
        <section className="hi-ui-manage-hosting-page__section">
          <Text variant="heading-2">Upgrade to premium hosting</Text>
          <Text variant="heading-4">
            High performance hosting without having to manage your own server
          </Text>
          <Text variant="body">
            Get faster page load speeds, handle even more traffic, host more websites and get a
            99.99% uptime guarantee with{' '}
            <Link href="/manage/order-managed-web-server.cgi">Premium Hosting</Link>. Our Premium
            Hosting platform offers a great combination of high powered servers coupled with our
            24/7 support. Because we manage the server setup, configuration and operating system
            updates, you do not need any additional technical knowledge to use these servers.
            Premium Hosting also uses the eXtend web hosting control panel, so each package on your
            premium server is managed in the same way as our standard packages. Treat your website
            with Premium Hosting today.{' '}
            <strong>Click Migrate to Premium Hosting to select a Premium Hosting package.</strong>{' '}
            You can purchase one <Link href="/manage/order-managed-web-server.cgi">here</Link>
            {salesEmail ? <> or contact sales at {salesEmail}.</> : '.'}
          </Text>
        </section>
      )}

      <Text variant="body">
        Are you a new hosting customer? Read our{' '}
        <Link href="/manage/quick-start-guide.cgi">Getting Started guide</Link> now!
      </Text>
      <Text variant="body">
        To change the primary domain name on any web hosting account, please{' '}
        <Link href="/manage/manage-dc-primary.cgi">click here</Link>.
      </Text>

      {/* Modals — only mounted for the selected account when their flag is on. */}
      {selected && migrationPro2Shared && (
        <Pro2SharedUpgradeModal
          isOpen={openModal === 'pro2shared'}
          onClose={() => setOpenModal(null)}
          packageOid={optionValue(selected)}
          paymentInterval={selected.paymentInterval === 'yearly' ? 'yearly' : 'monthly'}
          currentTier={selected.tier}
          upgradeGrid={upgradeGrid}
        />
      )}
      {selected && premiumMigrationEnabled && !hasPremiumHosting && (
        <PremiumUpsellModal isOpen={openModal === 'upsell'} onClose={() => setOpenModal(null)} />
      )}
      {selected && premiumMigrationEnabled && hasPremiumHosting && (
        <MigrateModal
          isOpen={openModal === 'migrate'}
          onClose={() => setOpenModal(null)}
          selectedPackages={[toMigratable(selected)]}
          onMigrationScheduled={() => setOpenModal(null)}
        />
      )}
    </>
  );
};
